import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ResetPasswordComponent, SignInComponent, SignUpComponent,
} from '@edx/frontend-component-authn-edx';
import { injectIntl } from 'gatsby-plugin-react-intl';
import {
  setOnboardingComponentResetPasswordClose,
  setOnboardingComponentResetPasswordOpen,
  setOnboardingComponentSignInClose,
  setOnboardingComponentSignInOpen,
  setOnboardingComponentSignUpClose,
  setOnboardingComponentSignUpOpen,
} from '@data/actions/onboardingComponent';
import { getAuthnMFELink } from '@utils/edxLinks';
import { getUserVariation } from '@prospectus/common/hooks/useInitOnboardingComponentExperiment';
import useOptimizelyIsInitialized from '@prospectus/common/hooks/useOptimizelyIsInitialized';
import isBrowser from './initialize';

function OnboardingComponentContainer({ userLoggedIn = true }) {
  if (!isBrowser) {
    return null;
  }

  const dispatch = useDispatch();
  const { isInitialized: optimizelyIsInitialized } = useOptimizelyIsInitialized();
  const isUserInOnboardingExperimentVariation = getUserVariation();

  const isOnboardingComponentSignInFormOpen = useSelector(
    state => state.onboardingComponent.isSignInFormOpen,
  );
  const isOnboardingComponentSignUpFormOpen = useSelector(
    state => state.onboardingComponent.isSignUpFormOpen,
  );
  const isOnboardingComponentResetPasswordFormOpen = useSelector(
    state => state.onboardingComponent.isResetPasswordFormOpen,
  );
  const onboardingComponentContext = useSelector(
    state => state.onboardingComponent.context,
  );

  // TODO: For now we will send all authenticated password reset traffic to authn.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let authMode = urlParams.get('authMode') || '';
    authMode = authMode.toLowerCase();

    if (isBrowser && userLoggedIn && authMode === 'passwordresetconfirm') {
      const passwordResetToken = urlParams.get('password_reset_token');
      urlParams.delete('password_reset_token');
      const redirectUrl = getAuthnMFELink(`/password_reset_confirm/${passwordResetToken}`);
      window.location.href = urlParams.toString() ? `${redirectUrl}?${urlParams.toString()}` : redirectUrl;
    }
  }, [userLoggedIn]);

  useEffect(() => {
    if (isBrowser && optimizelyIsInitialized && !userLoggedIn) {
      const urlParams = new URLSearchParams(window.location.search);
      let authMode = urlParams.get('authMode') || '';
      authMode = authMode.toLowerCase();

      if (isUserInOnboardingExperimentVariation) {
        if (authMode === 'login') {
          dispatch(setOnboardingComponentSignInOpen());
        } else if (authMode === 'register') {
          dispatch(setOnboardingComponentSignUpOpen());
        } else if (authMode === 'passwordresetconfirm') {
          dispatch(setOnboardingComponentResetPasswordOpen());
        }
      } else if (!isUserInOnboardingExperimentVariation) {
        let redirectUrl = '';
        urlParams.delete('authMode');
        if (authMode === 'login') {
          redirectUrl = getAuthnMFELink('/login');
          window.location.href = urlParams.toString() ? `${redirectUrl}?${urlParams.toString()}` : redirectUrl;
        } else if (authMode === 'register') {
          redirectUrl = getAuthnMFELink('/register');
          window.location.href = urlParams.toString() ? `${redirectUrl}?${urlParams.toString()}` : redirectUrl;
        } else if (authMode === 'passwordresetconfirm') {
          const passwordResetToken = urlParams.get('password_reset_token');
          urlParams.delete('password_reset_token');
          redirectUrl = getAuthnMFELink(`/password_reset_confirm/${passwordResetToken}`);
          window.location.href = urlParams.toString() ? `${redirectUrl}?${urlParams.toString()}` : redirectUrl;
        }
      }
    }
  }, [isBrowser, userLoggedIn, optimizelyIsInitialized, isUserInOnboardingExperimentVariation]);

  return (
    <>
      <SignUpComponent
        close={() => dispatch(setOnboardingComponentSignUpClose())}
        isOpen={isOnboardingComponentSignUpFormOpen}
        context={onboardingComponentContext}
      />
      <SignInComponent
        close={() => dispatch(setOnboardingComponentSignInClose())}
        isOpen={isOnboardingComponentSignInFormOpen}
        context={onboardingComponentContext}
      />
      <ResetPasswordComponent
        close={() => dispatch(setOnboardingComponentResetPasswordClose())}
        isOpen={isOnboardingComponentResetPasswordFormOpen}
      />
    </>
  );
}

OnboardingComponentContainer.propTypes = {
  // eslint-disable-next-line react/require-default-props
  userLoggedIn: PropTypes.bool,
};

export default injectIntl(OnboardingComponentContainer);
